import TranslationKeys from "../../../../contexts/I18nContext/translationKeys";
import {
  WhmAutocomplete,
  WhmBox,
  WhmFormHelperText,
  WhmStack,
  WhmTextField,
  whmTheme,
  WhmTooltip,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { InfoCard } from "../../shared/InfoCard";
import React, { useContext } from "react";
import { getDropdownValues } from "./entityTypeList";
import { I18nContext } from "../../../../contexts/I18nContext";
import {
  columnBox,
  placeholderTextFieldStyle,
  textFieldStyle,
  wrapBox,
} from "../../shared/styles";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import { iconSmall } from "../../../shared/iconThemes";
import { useFormikContext } from "formik";
import moment, { Moment } from "moment";
import DatePickerInput from "../../shared/ProfileDatePickerInput";
import HiddenField from "../../../shared/HiddenField";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../../../utils/yupUtils";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { EntityKYBFormType } from "./validations";
import { useFlaggedFieldState } from "./entityFieldKeyMap";
import { FlaggedFieldContainer } from "./FlaggedFieldCallout";

interface EntityInformationEditableCardProps {
  kybPending: boolean;
  onNotifyValueChange: (data: EntityKYBFormType) => void;
  flaggedFields: Map<string, string>;
  investorId: string;
  requestedUpdatesRoundId?: string;
}

export const EntityInformationEditableCard: React.FC<
  React.PropsWithChildren<EntityInformationEditableCardProps>
> = ({
  kybPending,
  onNotifyValueChange,
  flaggedFields,
  investorId,
  requestedUpdatesRoundId,
}) => {
  const { getTranslation, language } = useContext(I18nContext);
  const investorTypeDropdownValues = getDropdownValues(getTranslation);
  const [showEntityIdNumber, setShowEntityIdNumber] = React.useState(false);

  const { handleBlur, values, errors, setFieldValue } =
    useFormikContext<EntityKYBFormType>();

  const shouldShowTaxIdHelperText = () =>
    !!(
      errors.entityInformation?.entityIdNumber &&
      errors.entityInformation.entityIdNumber !==
        REQUIRED_GENERIC_HIDDEN_MESSAGE
    );

  const entityNameFlag = useFlaggedFieldState(
    investorId,
    "entityInformation.entityName",
    values.entityInformation.entityName,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const entityTypeFlag = useFlaggedFieldState(
    investorId,
    "entityInformation.entityType",
    values.entityInformation.entityType ?? "",
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const incorporationDateFlag = useFlaggedFieldState(
    investorId,
    "entityInformation.incorporationDate",
    values.entityInformation.incorporationDate?.toString() ?? "",
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const entityIdNumberFlag = useFlaggedFieldState(
    investorId,
    "entityInformation.entityIdNumber",
    values.entityInformation.entityIdNumber,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const businessesFlag = useFlaggedFieldState(
    investorId,
    "entityInformation.businesses",
    values.entityInformation.businesses,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const hasFlaggedFields =
    entityNameFlag.isFlagged ||
    entityTypeFlag.isFlagged ||
    incorporationDateFlag.isFlagged ||
    entityIdNumberFlag.isFlagged ||
    businessesFlag.isFlagged;

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_SUBTITLE
          }
        />
      }
      footer={
        !kybPending && (
          <TranslateHtml
            translationKey={
              TranslationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_FOOTER
            }
          />
        )
      }
    >
      <WhmBox
        sx={{
          ...columnBox,
          marginTop: "32px",
        }}
      >
        {(errors.entityInformation || hasFlaggedFields) && (
          <WhmFormHelperText error sx={{ marginBottom: "16px !important" }}>
            <Translate
              translationKey={
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_GENERIC_SECTION_ERROR
              }
            />
          </WhmFormHelperText>
        )}

        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
          }}
        >
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              entityNameFlag.isFlagged ? entityNameFlag.comment : undefined
            }
          >
            <FormikWhmTextField<EntityKYBFormType>
              name="entityInformation.entityName"
              dataTestId={"profile-entity-information.entity-name-field"}
              onBlur={(event) => {
                onNotifyValueChange(values);
                handleBlur(event);
              }}
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_NAME
                  }
                />
              }
              disabled={!kybPending && !entityNameFlag.isFlagged}
              error={
                !!errors.entityInformation?.entityName ||
                entityNameFlag.isFlagged
              }
            />
          </FlaggedFieldContainer>
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              entityTypeFlag.isFlagged ? entityTypeFlag.comment : undefined
            }
          >
            <WhmAutocomplete
              options={investorTypeDropdownValues}
              value={
                investorTypeDropdownValues.find((option) => {
                  return option.value === values.entityInformation.entityType;
                }) || null
              }
              onChange={(_, newValue) => {
                void setFieldValue(
                  "entityInformation.entityType",
                  newValue ? newValue.value : null,
                );
                onNotifyValueChange({
                  ...values,
                  entityInformation: {
                    ...values.entityInformation,
                    entityType: newValue?.value,
                  },
                });
              }}
              fullWidth
              onBlur={handleBlur}
              disabled={!kybPending && !entityTypeFlag.isFlagged}
              clearText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLEAR)}
              openText={getTranslation(TranslationKeys.AUTOCOMPLETE_OPEN)}
              closeText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLOSE)}
              renderInput={(props) => (
                // We don't want a FormikWhmTextField here because we don't want to connect it with the values.
                // That will be handled by the auto complete input.
                <WhmTextField
                  {...props}
                  error={
                    !!errors.entityInformation?.entityType ||
                    entityTypeFlag.isFlagged
                  }
                  placeholder={getTranslation(
                    TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_TYPE_PLACEHOLDER,
                  )}
                  inputProps={{
                    // eslint-disable-next-line react/prop-types
                    ...props.inputProps,
                    "data-test-id":
                      "profile-entity-information-entity-type-field",
                  }}
                  label={
                    <Translate
                      translationKey={
                        TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_ENTITY_TYPE
                      }
                    />
                  }
                />
              )}
            />
          </FlaggedFieldContainer>
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              incorporationDateFlag.isFlagged
                ? incorporationDateFlag.comment
                : undefined
            }
          >
            <DatePickerInput
              label={
                <Translate
                  translationKey={
                    TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE
                  }
                />
              }
              language={language}
              helperText={
                !incorporationDateFlag.isFlagged &&
                kybPending &&
                (errors.entityInformation?.incorporationDate
                  ? errors.entityInformation.incorporationDate
                  : getTranslation(
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_FORMAT,
                    ))
              }
              error={
                !!errors.entityInformation?.incorporationDate ||
                incorporationDateFlag.isFlagged
              }
              disabled={!kybPending && !incorporationDateFlag.isFlagged}
              value={
                values.entityInformation.incorporationDate
                  ? moment.utc(values.entityInformation.incorporationDate)
                  : moment.invalid()
              }
              disableFuture
              autoFocus={false}
              dataTestId="profile-entity-information-incorporation-date-field"
              name="entityInformation.incorporationDate"
              reduceAnimations
              onChange={(event) => {
                const date = event.target.value as unknown as
                  | Moment
                  | undefined;
                const newValue =
                  date && date.isValid() ? date.utc().toDate() : undefined;
                void setFieldValue(
                  "entityInformation.incorporationDate",
                  newValue,
                );
                onNotifyValueChange({
                  ...values,
                  entityInformation: {
                    ...values.entityInformation,
                    incorporationDate: newValue,
                  },
                });
              }}
              onBlur={(_) => {
                onNotifyValueChange(values);
              }}
              placeholder={getTranslation(
                TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_INCORPORATION_DATE_PLACEHOLDER,
              )}
            />
          </FlaggedFieldContainer>
        </WhmBox>

        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
            marginTop: "16px",
            alignItems: "flex-start",
          }}
        >
          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              entityIdNumberFlag.isFlagged
                ? entityIdNumberFlag.comment
                : undefined
            }
          >
            <WhmStack direction="row" alignItems="center">
              <HiddenField<EntityKYBFormType>
                id="entity-information-tax-id-field"
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_TAX_ID
                    }
                  />
                }
                field="entityInformation.entityIdNumber"
                fieldKind="sensitiveData"
                sx={{ flexGrow: 1, marginRight: "8px" }}
                showField={showEntityIdNumber}
                setShowField={setShowEntityIdNumber}
                error={
                  !!errors.entityInformation?.entityIdNumber ||
                  entityIdNumberFlag.isFlagged
                }
                helperText={
                  !entityIdNumberFlag.isFlagged &&
                  shouldShowTaxIdHelperText() &&
                  errors.entityInformation?.entityIdNumber
                }
                onBlur={(event) => {
                  void setFieldValue(
                    "entityInformation.entityIdNumber",
                    event.target.value.trim(),
                  );
                  onNotifyValueChange(values);
                  handleBlur(event);
                }}
                maskDisabled={!kybPending && !entityIdNumberFlag.isFlagged}
                disabled={!kybPending && !entityIdNumberFlag.isFlagged}
              />
              <WhmTooltip
                placement="top-start"
                data-test-id="profile-entity-information-tax-id-tooltip"
                title={
                  <TranslateHtml
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_TAX_ID_TOOLTIP
                    }
                  />
                }
              >
                <InfoOutlined
                  className={"tooltipIcon"}
                  style={iconSmall}
                  fill={whmTheme.palette.action.active}
                />
              </WhmTooltip>
            </WhmStack>
          </FlaggedFieldContainer>

          <FlaggedFieldContainer
            sx={textFieldStyle}
            comment={
              businessesFlag.isFlagged ? businessesFlag.comment : undefined
            }
          >
            <WhmStack direction="row" alignItems="center">
              <FormikWhmTextField
                name="entityInformation.businesses"
                dataTestId={"profile-entity-information-dba-field"}
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_DBA
                    }
                  />
                }
                onBlur={(event) => {
                  onNotifyValueChange(values);
                  handleBlur(event);
                }}
                sx={{ flexGrow: 1, marginRight: "8px" }}
                disabled={!kybPending && !businessesFlag.isFlagged}
                error={
                  !!errors.entityInformation?.businesses ||
                  businessesFlag.isFlagged
                }
              />
              <WhmTooltip
                placement="top-start"
                data-test-id="profile-entity-information-dba-tooltip"
                title={
                  <TranslateHtml
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_ENTITY_INFORMATION_CARD_DBA_TOOLTIP
                    }
                  />
                }
              >
                <InfoOutlined
                  style={iconSmall}
                  fill={whmTheme.palette.action.active}
                />
              </WhmTooltip>
            </WhmStack>
          </FlaggedFieldContainer>

          <WhmBox sx={placeholderTextFieldStyle} />
        </WhmBox>
      </WhmBox>
    </InfoCard>
  );
};
